import { Box, Typography } from "@mui/material";
import NextLink from "next/link";
import Kicker from "@/src/components/common/broadsheet/kicker";
import {
  FONT_TEXT,
  FONT_UI,
  hairline,
} from "@/src/components/common/broadsheet/tokens";

const q = (value: string) => encodeURIComponent(value.trim());

const linkSx = {
  fontFamily: FONT_UI,
  fontSize: "0.6875rem",
  fontWeight: 700,
  letterSpacing: "0.04em",
  color: "custom.link",
  textDecoration: "none",
  paddingBlock: "0.25rem",
  "&:hover": {
    color: "custom.linkHover",
    textDecoration: "underline",
    textUnderlineOffset: "0.15em",
  },
};

const ArticleDiscovery = ({
  authors,
  articleTitle,
}: {
  authors: Author[];
  articleTitle: string;
}) => {
  const named = (authors ?? []).filter(
    (author) => author.first_name || author.last_name,
  );
  const title = articleTitle ?? "";

  const elsewhere = [
    {
      label: "PubMed",
      href: `https://www.ncbi.nlm.nih.gov/sites/entrez?cmd=search&db=pubmed&term=${q(title)}`,
    },
    {
      label: "Google Scholar",
      href: `https://scholar.google.com/scholar?q=${q(title)}`,
    },
    {
      label: "Create citation alert",
      href: `https://scholar.google.com/scholar_alerts?view_op=create_alert_options&hl=en&alert_query=allintitle:%22${q(title)}%22&alert_params=hl%3Den%26as_sdt%3D2000`,
    },
  ];

  if (named.length === 0 && !title) return null;

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
        columnGap: 2.5,
        rowGap: 1,
        mt: 1,
        pt: 0.75,
        borderTop: "1px solid",
        borderColor: "custom.ruleHairline",
        "& > *": { minWidth: 0 },
      }}
    >
      {named.length > 0 ? (
        <Box>
          <Kicker component="p" sx={{ mb: 0.25 }}>
            More by these authors
          </Kicker>
          {named.map((author, index) => {
            const name =
              `${author.first_name ?? ""} ${author.last_name ?? ""}`.trim();

            return (
              <Box
                key={`discovery-author-${index}`}
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "baseline",
                  columnGap: 0.5,
                  py: 0.1875,
                  ...hairline,
                }}
              >
                <Typography
                  component="span"
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    fontFamily: FONT_TEXT,
                    fontSize: "0.8125rem",
                    lineHeight: 1.4,
                    color: "custom.ink",
                  }}
                >
                  {name}
                </Typography>
                <Box
                  component={NextLink}
                  href={`https://pubmed.ncbi.nlm.nih.gov/?term=${q(name)}%5Bau%5D&dispmax=50`}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`${name} on PubMed`}
                  sx={linkSx}
                >
                  PubMed
                </Box>
                <Box
                  component={NextLink}
                  href={`https://scholar.google.com/citations?view_op=search_authors&mauthors=author:%27${q(name)}%27&hl=en&oi=ao`}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`${name} on Google Scholar`}
                  sx={linkSx}
                >
                  Scholar
                </Box>
              </Box>
            );
          })}
        </Box>
      ) : null}

      {title ? (
        <Box>
          <Kicker component="p" sx={{ mb: 0.25 }}>
            Find related work
          </Kicker>
          {elsewhere.map((item) => (
            <Box
              key={item.label}
              sx={{ py: 0.1875, ...hairline }}
            >
              <Box
                component={NextLink}
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: "block",
                  fontFamily: FONT_TEXT,
                  fontSize: "0.8125rem",
                  lineHeight: 1.4,
                  color: "custom.link",
                  textDecoration: "none",
                  paddingBlock: "0.1875rem",
                  "&:hover": {
                    color: "custom.linkHover",
                    textDecoration: "underline",
                    textUnderlineOffset: "0.15em",
                  },
                }}
              >
                {item.label}
              </Box>
            </Box>
          ))}
        </Box>
      ) : null}
    </Box>
  );
};

export default ArticleDiscovery;
